import { Product } from '@shared/components/ui/Product';
import { UserCard } from '@shared/components/ui/usercard';
import { faBagShopping, faBox } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { CustomColDef } from '@shared/components/ui/AgGridTable';
import { formatCurrencyAmount, useCurrency } from '@modules/Orders/shared/hooks/useCurrency';
import { ColumnDef } from '@tanstack/react-table';
import { ColDef } from 'ag-grid-community';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { DropdownActions } from '../components/interaction/DropdownActions';
import { ProductsModel } from '../models/Products';

interface UseProductsAgGridColumnsProps {
  hasTeamData: boolean;
  selectedColumns?: string[];
  onStockClick?: (product: ProductsModel) => void;
  onReservedClick?: (product: ProductsModel) => void;
  refetch?: () => void;
}

const formatDate = (date?: string | null) => {
  if (!date) {
    return '-';
  }

  const parsed = new Date(date);
  if (Number.isNaN(parsed.getTime())) {
    return '-';
  }

  return parsed.toLocaleDateString();
};

const convertToTanStackColumns = (agGridColumns: ColDef[]): ColumnDef<ProductsModel>[] =>
  agGridColumns
    .filter((column) => {
      const field = column.field || column.headerName || 'unknown';
      return field !== 'actions' && field !== 'unknown';
    })
    .map((column) => ({
      id: column.field || column.headerName || 'unknown',
      accessorKey: column.field || column.headerName || 'unknown',
      header: column.headerName || 'Columna',
    }));

export const useProductsAgGridColumns = ({
  hasTeamData,
  selectedColumns = [],
  onStockClick,
  onReservedClick,
  refetch,
}: UseProductsAgGridColumnsProps) => {
  const { t } = useTranslation('productsModule');
  const currency = useCurrency();

  const allColumnDefs: ColDef[] = useMemo(() => {
    const baseColumns: CustomColDef[] = [
      {
        field: 'name',
        headerName: t('table.product'),
        sortable: true,
        minWidth: 250, // Ancho mínimo para el componente Product (avatar + nombre + código)
        cellRenderer: (params: any) => {
          const product = params.data as ProductsModel;
          return (
            <Product
              id={product.id}
              name={product.name}
              src={product.image_hash}
              code={product.code}
              showImageModal={true}
            />
          );
        },
      },
      {
        field: 'code',
        headerName: t('table.code'),
        sortable: true,
        cellRenderer: (params: any) => {
          const product = params.data as ProductsModel;
          return <span className="text-sm font-mono">{product.code}</span>;
        },
      },
      {
        field: 'cost',
        headerName: t('table.cost'),
        sortable: false,
        valueGetter: (params) => params.data.cost ?? null,
        cellRenderer: (params: any) => {
          const product = params.data as ProductsModel;
          return (
            <span className="text-sm">{product.cost != null ? formatCurrencyAmount(product.cost, currency) : '-'}</span>
          );
        },
      },
      {
        field: 'stock.disponible',
        headerName: t('table.available'),
        sortable: false,
        valueGetter: (params) => params.data.stock?.disponible ?? 0,
        cellRenderer: (params: any) => {
          const product = params.data as ProductsModel;
          return <span className="text-sm">{product.stock?.disponible ?? '-'}</span>;
        },
      },
      {
        field: 'stock.reservado',
        headerName: t('table.reserved'),
        sortable: false,
        valueGetter: (params) => params.data.stock?.reservado ?? 0,
        cellRenderer: (params: any) => {
          const product = params.data as ProductsModel;
          return (
            <button
              type="button"
              className="text-sm flex items-center gap-2 hover:underline"
              onClick={() => onReservedClick?.(product)}
            >
              <FontAwesomeIcon icon={faBagShopping} className="h-3 w-3" />
              {product.stock?.reservado ?? '-'}
            </button>
          );
        },
      },
      {
        field: 'stock.total',
        headerName: t('table.stock'),
        sortable: false,
        valueGetter: (params) => {
          const stock = params.data.stock || {};
          return (stock.disponible ?? 0) + (stock.entrante ?? 0) + (stock.reservado ?? 0);
        },
        cellRenderer: (params: any) => {
          const product = params.data as ProductsModel;
          const stock = product.stock || {};
          const total = (stock.disponible ?? 0) + (stock.entrante ?? 0) + (stock.reservado ?? 0);
          return (
            <button
              type="button"
              className="text-sm flex items-center gap-2 hover:underline"
              onClick={() => onStockClick?.(product)}
            >
              <FontAwesomeIcon icon={faBox} className="h-3 w-3" />
              {total}
            </button>
          );
        },
      },
      {
        field: 'created_at',
        headerName: t('table.created_at'),
        sortable: true,
        valueGetter: (params) => params.data.created_at,
        cellRenderer: (params: any) => {
          const product = params.data as ProductsModel;
          return <span className="text-sm">{formatDate(product.created_at)}</span>;
        },
      },
    ];

    if (hasTeamData) {
      baseColumns.push({
        field: 'client',
        headerName: t('table.client'),
        sortable: true,
        valueGetter: (params) => params.data.team?.name,
        cellRenderer: (params: any) => {
          const product = params.data as ProductsModel;
          if (!product.team?.name) {
            return <span className="text-sm text-complementary-text">-</span>;
          }
          return <UserCard name={product.team.name} email="" />;
        },
      });
    }

    baseColumns.push({
      field: 'actions',
      headerName: '',
      width: 50,
      minWidth: 50,
      maxWidth: 50,
      resizable: false,
      pinned: 'right',
      lockPosition: 'right',
      suppressMovable: true,
      suppressSizeToFit: true,
      suppressAutoSize: true,
      cellRenderer: (params: any) => (
        <div className="flex justify-end w-full">
          <DropdownActions product={params.data} refetch={refetch} />
        </div>
      ),
    });

    return baseColumns;
  }, [t, currency, hasTeamData, onStockClick, onReservedClick, refetch]);

  const filteredColumnDefs = useMemo(() => {
    const alwaysVisible = allColumnDefs.filter((column) => {
      const field = column.field || column.headerName || 'unknown';
      return field === 'actions' || field === 'name';
    });

    const selectableColumns = allColumnDefs.filter((column) => {
      const field = column.field || column.headerName || 'unknown';
      return field !== 'actions';
    });

    if (!selectedColumns.length) {
      return [...selectableColumns, ...alwaysVisible].reduce<ColDef[]>((acc, column) => {
        const field = column.field || column.headerName || 'unknown';
        if (!acc.some((existing) => (existing.field || existing.headerName || 'unknown') === field)) {
          acc.push(column);
        }
        return acc;
      }, []);
    }

    const selectedDefs = selectableColumns.filter((column) => {
      const field = column.field || column.headerName || 'unknown';
      return selectedColumns.includes(field);
    });

    const combined = [...selectedDefs];
    alwaysVisible.forEach((column) => {
      const field = column.field || column.headerName || 'unknown';
      if (!combined.some((existing) => (existing.field || existing.headerName || 'unknown') === field)) {
        combined.push(column);
      }
    });

    return combined;
  }, [allColumnDefs, selectedColumns]);

  const tanStackColumns = useMemo(() => convertToTanStackColumns(allColumnDefs), [allColumnDefs]);

  return {
    allColumnDefs,
    filteredColumnDefs,
    tanStackColumns,
  };
};
